import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { HelpCircle } from 'lucide-react';
import { Link } from '@tanstack/react-router';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@bliks-io/ui';
import { NavBadge, NavMain, type NavMainItem } from './NavMain';
import { filterNavigation } from './filterNavigation';
import { buildAppNavItems } from './buildAppNavItems';
import { mainNavigation } from './navigationConfig';
import { countTasks, useTasks } from '@/api/queries/useTasks';
import { useMyOpenTasks } from '@/api/queries/useMyOpenTasks';
import { useSupportTicketsWithNewReply } from '@/api/queries/useSupportTickets';
import { useInstalledApps } from '@/api/queries/useInstalledApps';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuthStore } from '@/stores/auth-store';

export function NavSidebar() {
  const { t, i18n } = useTranslation();
  const { data: tasks } = useTasks();
  const { data: openTasks } = useMyOpenTasks();
  const { data: installedApps } = useInstalledApps();
  const { can } = usePermissions();
  const slug = useAuthStore().slug();

  const taskCount = countTasks(tasks, openTasks);
  const { data: ticketsWithNewReply = 0 } = useSupportTicketsWithNewReply();

  // Filter navigation items based on user permissions
  const filteredNavigation = useMemo(() => filterNavigation(mainNavigation, can), [can]);

  // Add badge count to My Tasks nav item
  const navigationWithBadges = useMemo(() => {
    return filteredNavigation.map((item) => {
      // Find the original index in mainNavigation to check if it's My Tasks
      const originalIndex = mainNavigation.findIndex((nav) => nav.title === item.title);
      // Add badge to "My Tasks" item (index 1 in original array) only if there are tasks
      if (originalIndex === 1 && taskCount > 0) {
        return { ...item, badge: taskCount };
      }
      return item;
    });
  }, [filteredNavigation, taskCount]);

  const appNavItems = useMemo<NavMainItem[]>(
    () => buildAppNavItems(installedApps, i18n.language),
    [installedApps, i18n.language]
  );

  return (
    <TooltipProvider delayDuration={0}>
      <nav className="flex flex-col w-14 bg-sidebar text-sidebar-foreground shrink-0">
        {/* Main navigation */}
        <div className="flex-1 overflow-y-auto">
          <NavMain items={navigationWithBadges} />
          {appNavItems.length > 0 && (
            <NavMain title={t('common.Sidebar.Apps')} items={appNavItems} />
          )}
        </div>

        {/* Footer */}
        <div className="p-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/$slug/help"
                params={{ slug }}
                className="flex items-center justify-center w-full h-10 transition-colors rounded-md hover:bg-sidebar-accent"
              >
                <div className="relative">
                  <HelpCircle
                    className="h-[21px] w-[21px] text-sidebar-foreground"
                    strokeWidth={1.5}
                  />
                  {ticketsWithNewReply > 0 && <NavBadge count={String(ticketsWithNewReply)} />}
                </div>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right" align="center">
              {t('common.Sidebar.Help')}
            </TooltipContent>
          </Tooltip>
        </div>
      </nav>
    </TooltipProvider>
  );
}
